import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Column, useSortBy, useTable } from 'react-table'
import moment from 'moment'
import { KTIcon } from '../../../../helpers'
import { DatePicker } from 'antd'
import { axiosClientAuth } from '../../../../../app/utils/axiosClient'
import { Modal, Spinner } from 'react-bootstrap'
import { useQuery, useQueryClient } from 'react-query'
import {
  categorizeItemsByStatus,
  categorizeItemsForBlacklist,
  getClassNameByStatus,
} from '../../../../../app/utils/helper'
import Swal from 'sweetalert2'
import { useLayout } from '../../../../layout/core'
import { RESOURCE_ANALYTICS_CACHE_KEY } from '../../../../../app/hooks/useResourceAnalytics'
import { useIntl } from 'react-intl'
import { REGULATORY_OPERATOR_KEY } from '../../../../../config/countryConfig'

type ManageIPType = {
  srno?: string
  address?: string
  created_at?: Date
  self_status: boolean | null
  status: Record<string, { active: boolean }>
  name: string
  uuid: string
  is_blacklisted_by_tcil: boolean | null
}

const parseIpOperatorStatuses = (status: Record<string, any> = {}) => {
  const operators = Object.entries(status).filter(([key]) => key !== 'self' && key !== REGULATORY_OPERATOR_KEY)
  const whitelisted = operators.filter(([, value]) => value?.active === true)
  const blacklisted = operators.filter(([, value]) => value?.active === false)
  return { operators, whitelisted, blacklisted }
}

const OperatorStatusModal = ({
  item,
  onClose,
}: {
  item: ManageIPType | null
  onClose: () => void
}) => {
  const intl = useIntl()
  if (!item) return null
  const { whitelisted, blacklisted } = parseIpOperatorStatuses(item?.status || {})
  return (
    <Modal show={!!item} onHide={onClose} centered size='lg'>
      <Modal.Header className='border-0 pb-0'>
        <div>
          <h4 className='mb-1 fw-bold'>{intl.formatMessage({ id: 'MYIP.MODAL.OPERATOR_STATUSES_TITLE' })}</h4>
          <p className='text-muted fs-7 mb-0'>{item?.address}</p>
        </div>
        <button onClick={onClose} className='btn btn-sm btn-icon btn-active-color-primary ms-auto'>
          <KTIcon iconName='cross' className='fs-1' />
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className='row g-4'>
          <div className='col-md-6'>
            <h6 className='text-success border-bottom border-success pb-2 mb-3'>
              {intl.formatMessage({ id: 'COMMON.STATUS.WHITELISTED_BY' })} ({whitelisted.length})
            </h6>
            {whitelisted.length === 0 ? (
              <p className='text-muted fs-7'>{intl.formatMessage({ id: 'MYIP.MESSAGES.NO_OPERATOR_WHITELISTED' })}</p>
            ) : (
              whitelisted.map(([name]) => (
                <div key={String(name)} className='border border-success rounded p-3 mb-2 bg-light-success'>
                  <span className='fw-bold text-success text-uppercase'>{String(name)}</span>
                </div>
              ))
            )}
          </div>
          <div className='col-md-6'>
            <h6 className='text-danger border-bottom border-danger pb-2 mb-3'>
              {intl.formatMessage({ id: 'COMMON.STATUS.BLACKLISTED_BY' })} ({blacklisted.length})
            </h6>
            {blacklisted.length === 0 ? (
              <p className='text-muted fs-7'>{intl.formatMessage({ id: 'MYIP.MESSAGES.NO_OPERATOR_BLACKLISTED' })}</p>
            ) : (
              blacklisted.map(([name]) => (
                <div key={String(name)} className='border border-danger rounded p-3 mb-2 bg-light-danger'>
                  <span className='fw-bold text-danger text-uppercase'>{String(name)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

const TableWidgetIPList = ({ className }) => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const { showLoader, hideLoader } = useLayout()
  const { RangePicker } = DatePicker

  const [operatorStatusItem, setOperatorStatusItem] = useState<ManageIPType | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // --- API-driven filter state ---
  const [searchInput, setSearchInput] = useState('')   // raw input value (shown in field)
  const [searchParam, setSearchParam] = useState('')   // debounced value sent to API
  const [activeFilter, setActiveFilter] = useState('') // '' | 'true' | 'false'
  const [fromDate, setFromDate] = useState('')          // YYYY-MM-DD
  const [toDate, setToDate] = useState('')              // YYYY-MM-DD

  // Debounce search: fire API after 400ms of no typing
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setSearchParam(value)
      setCurrentPage(1)
    }, 400)
  }

  // Whenever any filter changes, reset to page 1
  const applyFilter = (updates: {
    active?: string
    from?: string
    to?: string
  }) => {
    if (updates.active !== undefined) setActiveFilter(updates.active)
    if (updates.from !== undefined) setFromDate(updates.from)
    if (updates.to !== undefined) setToDate(updates.to)
    setCurrentPage(1)
  }

  const queryKey = ['ip-list', currentPage, pageSize, searchParam, activeFilter, fromDate, toDate]

  const { data: ipListResponse, isLoading, isFetching } = useQuery(
    queryKey,
    async () => {
      const params: Record<string, any> = {
        page: currentPage,
        page_size: pageSize,
      }
      if (searchParam) params.search = searchParam
      if (activeFilter !== '') params.active = activeFilter      // 'true' | 'false'
      if (fromDate) params.from_date = fromDate
      if (toDate) params.to_date = toDate

      const res = await axiosClientAuth.get('/ip/', { params })
      const responseData = res.data?.data || res.data || {}
      const results = responseData?.results || []

      const mappedIpData = results.map((item: any) => ({
        ...item,
        uuid: item?.uuid ?? item?.id,
        name: item?.name ?? item?.title,
        status: item?.status || {},
        self_status: item?.status?.self?.active,
        is_blacklisted_by_tcil:
          item?.status?.[REGULATORY_OPERATOR_KEY]?.active === undefined
            ? null
            : item?.status?.[REGULATORY_OPERATOR_KEY]?.active === false,
      }))

      return {
        ipaddress: mappedIpData,
        count: responseData?.count ?? mappedIpData.length,
        page: responseData?.page ?? currentPage,
        total_pages: responseData?.total_pages ?? 1,
      }
    },
    { keepPreviousData: true }
  )

  const tableData = useMemo(() => ipListResponse?.ipaddress || [], [ipListResponse])

  const columns: ReadonlyArray<Column<ManageIPType>> = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.SERIAL_NO' }),
        accessor: 'srno',
        Cell: ({ ...props }) => (
          <>{(currentPage - 1) * pageSize + props.row.index + 1}</>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'MYIP.TABLE.IP_NAME' }),
        accessor: 'name',
        Cell: ({ ...props }) => <span>{props.value}</span>,
      },
      {
        Header: intl.formatMessage({ id: 'MYIP.TABLE.IP_ADDRESS' }),
        accessor: 'address',
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.DATE' }),
        accessor: 'created_at',
        Cell: ({ ...props }) => (
          <>
            <span>{moment(props.value).format('DD MMM YYYY')}</span>
            <span className='d-block text-muted fw-semibold'>
              {moment(props.value).format('LT')}
            </span>
          </>
        ),
      },
      {
        Header: 'SELF DECLARATION',  // kept as-is (self-declaration is a specific term)
        accessor: 'self_status',
        Cell: ({ ...props }) => (
          <span
            className={`badge badge-light-${getClassNameByStatus(props.value)} fw-bold px-4 py-3`}
          >
            {categorizeItemsByStatus(props.value)}
          </span>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'MYIP.TABLE.OPERATOR_STATUSES' }),
        id: 'operator-statuses',
        Cell: ({ ...props }) => {
          const { whitelisted, blacklisted } = parseIpOperatorStatuses(
            props?.row?.original?.status || {}
          )
          return (
            <button
              className='btn btn-sm btn-light d-flex align-items-center gap-2 fw-semibold'
              onClick={() => setOperatorStatusItem(props.row.original)}
            >
              <i className='bi bi-people-fill text-gray-500' />
              View
              <span className='text-success fw-bold'>{whitelisted.length}</span>
              <span className='text-muted'>.</span>
              <span className='text-danger fw-bold'>{blacklisted.length}</span>
            </button>
          )
        },
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.ACTION' }),
        id: 'action',
        Cell: ({ ...props }) => (
          <>
            <button
              className='btn h-35px btn-icon btn btn-light-primary me-3'
              onClick={() => onStatusChange(props.row.original, true)}
              disabled={Boolean(props?.row?.original?.self_status)}
            >
              <KTIcon iconName='check' className='text-primary text-hover-white fs-1' />
            </button>
            <button
              className='btn h-35px btn-icon btn btn-light-danger'
              onClick={() => onStatusChange(props.row.original, false)}
              disabled={
                props?.row?.original?.self_status != null &&
                !Boolean(props?.row?.original?.self_status)
              }
            >
              <KTIcon iconName='cross' className='text-danger text-hover-white fs-1' />
            </button>
          </>
        ),
      },
    ],
    [currentPage, pageSize] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // Pure display table — no client-side filtering or pagination plugins needed
  const IPListTable = useTable(
    {
      columns: columns as readonly Column<object>[],
      data: tableData,
    },
    useSortBy
  )

  const onStatusChange = async (ipItem: any, status: boolean) => {
    showLoader()
    try {
      const ipId = ipItem?.uuid ?? ipItem?.id
      const res = await axiosClientAuth.patch(`/ip/${ipId}/status/`, {
        active: status,
        reason: status ? 'Manually enabled from IP list' : 'Manually disabled from IP list',
      })
      if (res.data.status) {
        Swal.fire({
          titleText: 'Success!',
          text: `IP ${status ? 'enabled' : 'disabled'} successfully.`,
          icon: 'success',
          confirmButtonText: 'Ok! Got It',
        })
        queryClient.invalidateQueries('ip-list')
        queryClient.invalidateQueries(RESOURCE_ANALYTICS_CACHE_KEY)
      }
    } catch (error) {
      console.log(error)
    } finally {
      hideLoader()
    }
  }

  const onDateFilterChange = (_value: any, datestring: any) => {
    if (!datestring?.length || (datestring[0] === '' && datestring[1] === '')) {
      applyFilter({ from: '', to: '' })
      return
    }
    // datestring is [startDate, endDate] in the picker's format — convert to YYYY-MM-DD
    applyFilter({
      from: datestring[0] ? moment(datestring[0]).format('YYYY-MM-DD') : '',
      to: datestring[1] ? moment(datestring[1]).format('YYYY-MM-DD') : '',
    })
  }

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{intl.formatMessage({ id: 'MYIP.TABLE.SECTION_TITLE' })}</span>
          <span className='text-muted fw-semibold fs-7'>
            Total {ipListResponse?.count || 0} IP
          </span>
        </h3>
        <div className='d-flex align-items-center flex-wrap gap-2'>
          {/* Date range filter */}
          <RangePicker
            className='bg-secondary-custom text-white border-0 shadow-none'
            style={{ height: '34px' }}
            onChange={onDateFilterChange}
            disabledDate={(current) => current && current.valueOf() > Date.now()}
          />

          {/* Self Declaration / Active filter */}
          <select
            className='form-select w-auto form-select-sm form-select-solid'
            value={activeFilter}
            onChange={(e) => applyFilter({ active: e.target.value })}
          >
            <option value=''>{intl.formatMessage({ id: 'COMMON.STATUS.ACTIVE' })}</option>
            <option value='true'>{intl.formatMessage({ id: 'COMMON.STATUS.ENABLED' })}</option>
            <option value='false'>{intl.formatMessage({ id: 'COMMON.STATUS.DISABLED' })}</option>
          </select>

          {/* Search */}
          <div className='d-flex align-items-center position-relative my-1'>
            <KTIcon iconName='magnifier' className='text-white-500 position-absolute ms-2' />
            <input
              type='text'
              className='form-control form-control-solid form-select-sm w-150px ps-9'
              placeholder={intl.formatMessage({ id: 'MYIP.TABLE.SEARCH_PLACEHOLDER' })}
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='table-responsive'>
          <table
            {...IPListTable.getTableProps()}
            className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
          >
            <thead>
              {IPListTable.headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      className={column.id === 'ACTION' || column.id === 'action' ? 'w-200px' : ''}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <span className='text-gray-400 fs-7'>{column.render('Header') as React.ReactNode}</span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <KTIcon iconName='down' className='text-gray ms-1' />
                        ) : (
                          <KTIcon iconName='up' className='text-gray ms-1' />
                        )
                      ) : (
                        ''
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...IPListTable.getTableBodyProps()}>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className='text-center'>
                    <Spinner animation='border' role='status' />
                  </td>
                </tr>
              ) : IPListTable.rows.length > 0 ? (
                IPListTable.rows.map((row) => {
                  IPListTable.prepareRow(row)
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td className='fw-bold' {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={7} className='text-center'>
                    <span className='fw-bold'>{intl.formatMessage({ id: 'COMMON.MESSAGES.NO_DATA_FOUND' })}</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <OperatorStatusModal
            item={operatorStatusItem}
            onClose={() => setOperatorStatusItem(null)}
          />

          <div className='d-flex justify-content-between align-items-center mt-5'>
            <div>
              <select
                className='form-select form-select-sm form-select-solid'
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value))
                  setCurrentPage(1)
                }}
              >
                {[5, 10, 20, 25, 50, 100].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </div>
            <div>
              <span>
                {intl.formatMessage({ id: 'COMMON.PAGINATION.PAGE' })}{' '}
                <strong>
                  {ipListResponse?.page || 1} {intl.formatMessage({ id: 'COMMON.PAGINATION.OF' })} {ipListResponse?.total_pages || 1}
                </strong>
              </span>
            </div>
            <div>
              <button
                className='btn btn-sm'
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage <= 1 || isFetching}
              >
                <KTIcon iconName='left' className='text-gray fs-1 me-5' />
              </button>
              <button
                className='btn btn-sm'
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(prev + 1, ipListResponse?.total_pages || 1)
                  )
                }
                disabled={currentPage >= (ipListResponse?.total_pages || 1) || isFetching}
              >
                <KTIcon iconName='right' className='text-gray fs-1 me-5' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TableWidgetIPList
